import { http } from '@/shared/api/httpClient';
import type {
  ApiClubCourt,
  ApiCourtBlock,
  ApiMatchPlanner,
  ApiSchedulePublication,
  AssignCourtSlotPayload,
  CourtBlockPayload,
  CreateCourtPayload,
  SchedulePublicationPayload,
  UpdateCourtPayload,
} from '../model/courtTypes';
import type { ApiMatch } from '../model/types';

/** Wywolania endpointow kortow klubu i kalendarza planowania meczow. */

/** Pobiera korty klubu wraz z przerwami technicznymi. */
export function getClubCourts(clubId: string): Promise<ApiClubCourt[]> {
  return http.get<ApiClubCourt[]>(`/competitions/club/${clubId}/courts`);
}

/** Zaklada kort klubu. Numer musi byc unikalny w klubie (409). */
export function createCourt(clubId: string, payload: CreateCourtPayload): Promise<ApiClubCourt> {
  return http.post<ApiClubCourt>(`/competitions/club/${clubId}/courts`, payload);
}

/** Edytuje kort (pola null pomijane). */
export function updateCourt(
  clubId: string,
  courtId: string,
  payload: UpdateCourtPayload,
): Promise<void> {
  return http.put<void>(`/competitions/club/${clubId}/courts/${courtId}`, payload);
}

/** Usuwa kort. Backend odmawia (409), gdy sa na nim zaplanowane mecze. */
export function deleteCourt(clubId: string, courtId: string): Promise<void> {
  return http.delete<void>(`/competitions/club/${clubId}/courts/${courtId}`);
}

/** Dodaje przerwe techniczna na korcie. */
export function addCourtBlock(
  clubId: string,
  courtId: string,
  payload: CourtBlockPayload,
): Promise<ApiCourtBlock> {
  return http.post<ApiCourtBlock>(
    `/competitions/club/${clubId}/courts/${courtId}/blocks`,
    payload,
  );
}

/** Usuwa przerwe techniczna z kortu. */
export function removeCourtBlock(
  clubId: string,
  courtId: string,
  blockId: string,
): Promise<void> {
  return http.delete<void>(`/competitions/club/${clubId}/courts/${courtId}/blocks/${blockId}`);
}

/**
 * Pobiera kalendarz planowania meczow klubu na jeden dzien: korty, aktywne
 * turnieje i ich mecze (rozplanowane + pula bez slotu).
 */
export function getMatchPlanner(clubId: string, date: string): Promise<ApiMatchPlanner> {
  return http.get<ApiMatchPlanner>(`/competitions/club/${clubId}/match-planner`, {
    query: { date },
  });
}

/**
 * Kladzie mecz na korcie w konkretnym dniu i godzinie (drop na kalendarzu).
 * Kolizja z innym meczem albo przerwa techniczna konczy sie 409 z opisem.
 */
export function assignCourtSlot(
  matchId: string,
  payload: AssignCourtSlotPayload,
): Promise<ApiMatch> {
  return http.put<ApiMatch>(`/matches/${matchId}/court-slot`, payload);
}

/** Zdejmuje mecz z kalendarza — wraca do puli do rozplanowania. */
export function clearCourtSlot(matchId: string): Promise<ApiMatch> {
  return http.delete<ApiMatch>(`/matches/${matchId}/court-slot`);
}

/** Pobiera widocznosc planu meczow klubu na ekranie prezentacji. */
export function getSchedulePublication(clubId: string): Promise<ApiSchedulePublication> {
  return http.get<ApiSchedulePublication>(`/competitions/club/${clubId}/schedule-publication`);
}

/** Upublicznia (`true`) albo utajnia (`false`) plan meczow klubu. */
export function setSchedulePublication(
  clubId: string,
  payload: SchedulePublicationPayload,
): Promise<ApiSchedulePublication> {
  return http.put<ApiSchedulePublication>(
    `/competitions/club/${clubId}/schedule-publication`,
    payload,
  );
}
